import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { UamErrorText, UamErrorTextContainer, UamRegistrationPanelContainer } from '../../../../styles-and-themes/styles/uam-registration-styles';

import PasswordAndConfirmPasswordControlSet from '../../ControlSets/PasswordAndConfirmPasswordControlSet';
import SecurityQuestionsAndAnswersControlSet from '../../ControlSets/SecurityQuestionsAndAnswersControlSet';
import MarketingOptInControlSet from '../../ControlSets/MarketingOptInControlSet';
import TermsOfServiceControlSet from '../../ControlSets/TermsOfServiceControlSet';
import BackAndSaveControlSet from '../../ControlSets/BackAndSaveControlSet/BackAndSaveControlSet';
import { areAllFieldsValid, makeInitialField } from '../../../../utils/field-utils';
import { composeDestinationQuerystring, getReturnUrlByDestination } from '../../../../../shared-with-server/destination-utils';
import { destinationInfo } from '../../../../destructured-front-end-config';

const makePromiseWithResolvers = () => {
  const questionOptionsPromise = {
    promise: null,
    resolveCb: null,
    rejectCb: null,
  };

  questionOptionsPromise.promise = new Promise((resolve, reject) => {
    questionOptionsPromise.resolveCb = resolve;
    questionOptionsPromise.rejectCb = reject;
  });

  return questionOptionsPromise;
};

const Step2RegistrationPanel = ({
  brand, 
  destPath, 
  setIsRegistrationComplete, 
  isNewRegistration,
  setIsBusy,
}) => {
  const questionOptionsPromise = makePromiseWithResolvers();

  const [isThirdPartySignIn, setIsThirdPartySignIn] = useState(false);

  const [password, setPassword] = useState(makeInitialField(''));
  const [confirmPassword, setConfirmPassword] = useState(makeInitialField(''));
  const [isRealPassword, setIsRealPassword] = useState(false); // indicates whether or not masked password value is real
  const [dataToSave, setDataToSave] = useState(null);

  const [countryOrRegion, setCountryOrRegion] = useState(null);

  const [questions, setQuestions] = useState([{
    value: '',
    state: 0,
  }, {
    value: '',
    state: 0,
  }, {
    value: '',
    state: 0,
  }]);

  const [answers, setAnswers] = useState([{
    value: '',
    state: 0,
  }, {
    value: '',
    state: 0,
  }, {
    value: '',
    state: 0,
  }]);
  const [termsOfService, setTermsOfService] = useState(false);
  const [optIn, setOptIn] = useState(null);

  const getCalculatedOptIn = () => {
    if (optIn !== null) { return optIn; }

    return countryOrRegion === 'United States';
  };

  const changeHandlersRef = useRef({
    password: null, // will be set by the PasswordAndConfirmPasswordControlSet
    confirmPassword: null, // will be set by the PasswordAndConfirmPasswordControlSet
    questions: null, // will be set by the SecurityQuestionsAndAnswersControlSet
    answers: null, // will be set by the SecurityQuestionsAndAnswersControlSet
    questionOptionsPromise, // will be resolved by the SecurityQuestionsAndAnswersControlSet
  });

  const changeHandlers = changeHandlersRef.current;

  const fieldsToCheck = [
    ...(isThirdPartySignIn ? [] : [...questions, ...answers]),
    ...(!isRealPassword || isThirdPartySignIn ? [] : [password, confirmPassword]),
  ];

  const allFieldsValid = areAllFieldsValid(fieldsToCheck);

  // save data effect to initiate save
  useEffect(() => {
    if (!dataToSave) { return; }

    axios.patch(
      `/api/v2/fi/multi-step-registration?${composeDestinationQuerystring(destinationInfo, destPath)}`,
      dataToSave,
    ).then((res) => {
      localStorage.removeItem('registration_info');

      if (res.data.automaticLogin) {
        window.location.href = getReturnUrlByDestination(destinationInfo, destPath.dest, destPath.path) || destPath.retURL; // Redirect to home
      } else {
        setIsBusy(false);
        setIsRegistrationComplete(true);
      }
    }).catch(() => {
      alert('Error saving');
      setIsBusy(false);
    });
  }, [dataToSave]);

  // empty effect to fetch data for page
  useEffect(
    () => {
      axios.get(
        '/api/v2/fi/multi-step-registration',
        { validateStatus: (status) => status < 500 },
      ).then((res) => {
        if (res.status === 401) {
          const message = res.data?.message === 'Missing registration token'
            ? 'Your registration verification token has expired or is missing. You will need to start over.'
            : 'An unexpected error occurred retrieving registration data. You will need to start over.';

          alert(message);
          window.location.assign(`/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`);
          return;
        }

        const step2Data = res.data.step2;

        setCountryOrRegion(res.data.step1.country); // from previous form, this dictates default behavior for opt-in

        if (step2Data.isPasswordDefined) {
          setIsRealPassword(false);
          setPassword({ value: 'deV5lRcbaLX@cGvZG$', state: 2 }); // random hard-coded value to present masked value in UI
          setConfirmPassword({ value: 'deV5lRcbaLX@cGvZG$', state: 2 }); // random hard-coded value to present masked value in UI
        } else if (!isNewRegistration) {
          changeHandlers.password('');
          changeHandlers.confirmPassword('');
        } else {
          setPassword({ value: '', state: 0 });
          setConfirmPassword({ value: '', state: 0 });
        }

        if (!isNewRegistration) {
          setOptIn(step2Data.optIn);
          setTermsOfService(step2Data.termsAndConditions);
        }

        // wait for question options to be loaded by subcontrol BEFORE setting questions and answers which relies on this data
        questionOptionsPromise.promise.then(() => {
          for (let i = 0; i < 3; i++) {
            if (!isNewRegistration) {
              changeHandlers.questions(i, step2Data.securityQuestionsAndAnswers[i]?.questionId || '');
              changeHandlers.answers(i, step2Data.securityQuestionsAndAnswers[i]?.answer || '');
            } else {
              const questionsCopy = structuredClone(questions);
              const answersCopy = structuredClone(answers);
              questionsCopy[i].value = step2Data.securityQuestionsAndAnswers[i]?.questionId || '';
              answersCopy[i].value = step2Data.securityQuestionsAndAnswers[i]?.answer || '';
              setQuestions(questionsCopy);
              setAnswers(answersCopy);
            }
          }
        });
      })
        .catch(() => {
          alert('An unexpected error occurred retrieving registration data. You will need to start over.');
          window.location.assign(`/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`);
        });
    },
    [],
  );

  // empty effect to get registration information from current registration token cookie
  useEffect(() => {
    axios.get('/api/v2/fi/token/registration-information').then((res) => {
      setIsThirdPartySignIn(res.data.isThirdPartySignIn);
    }).catch(() => {
      alert('An unexpected error occurred retrieving registration information. You will need to start over.');
      window.location.assign(`/uam/login?${composeDestinationQuerystring(destinationInfo, destPath)}`);
    });
  }, []);

  return (
    <>
      <UamRegistrationPanelContainer>
        <UamErrorTextContainer><UamErrorText>All fields are required</UamErrorText></UamErrorTextContainer>
        <h2>Finish setting up your account</h2>

        {
          !isThirdPartySignIn && (
            <>
              <PasswordAndConfirmPasswordControlSet
                password={password}
                setPassword={setPassword}
                confirmPassword={confirmPassword}
                setConfirmPassword={setConfirmPassword}
                isRealPassword={isRealPassword}
                setIsRealPassword={setIsRealPassword}
                changeHandlers={changeHandlers}
              />

              <SecurityQuestionsAndAnswersControlSet
                questions={questions}
                answers={answers}
                setQuestions={setQuestions}
                setAnswers={setAnswers}
                changeHandlers={changeHandlers}
              />
            </>
          )
        }

        <TermsOfServiceControlSet
          brand={brand}
          termsOfService={termsOfService}
          setTermsOfService={setTermsOfService}
        />

        <MarketingOptInControlSet
          brand={brand}
          optIn={optIn}
          setOptIn={setOptIn}
          getCalculatedOptIn={getCalculatedOptIn}
        />

        <BackAndSaveControlSet
          allFieldsValid={allFieldsValid && termsOfService}
          onBack={() => {
            window.location.assign(`/uam/step1?${composeDestinationQuerystring(destinationInfo, destPath)}`);
          }}
          onSave={() => {
            if (!allFieldsValid || !termsOfService) {
              alert('Please fill out all fields and agree to the terms and conditions.'); // This should never happen, but just in case
              return;
            }

            const dataToSaveCopy = isThirdPartySignIn ? {
              tpTermsAndConditions: termsOfService,
              tpOptIn: optIn === null ? getCalculatedOptIn() : optIn,
            } : {
              securityQuestionsAndAnswers: [
                { questionId: questions[0].value, answer: answers[0].value },
                { questionId: questions[1].value, answer: answers[1].value },
                { questionId: questions[2].value, answer: answers[2].value },
              ],
              termsAndConditions: termsOfService,
              optIn: optIn === null ? getCalculatedOptIn() : optIn,
            };

            if (!isThirdPartySignIn && isRealPassword) {
              dataToSaveCopy.password = password.value;
            }

            setIsBusy(true);
            setDataToSave(dataToSaveCopy);
          }}
        />
      </UamRegistrationPanelContainer>
    </>
  );
};

Step2RegistrationPanel.propTypes = {
  brand: PropTypes.object.isRequired,
  destPath: PropTypes.object.isRequired,
  setIsRegistrationComplete: PropTypes.func.isRequired,
  isNewRegistration: PropTypes.bool.isRequired,
  setIsBusy: PropTypes.func.isRequired,
};

export default Step2RegistrationPanel;
